import React, { FC, useContext, useState, useCallback, useEffect, useRef } from 'react';

import { Call, Device } from '@twilio/voice-sdk';

import { getUserTwilioToken, updateAgentPhoneAssignee, getAgentStatus } from '../../api';
import { TWILIO_SIGNALING_ENDPOINT } from '../../envs';
import useTwilioCalls from '../../hooks/useTwilioCalls';
import { useAgentHeartbeat } from '../../hooks/useAgentHeartbeat';
import { useCrossTabSync } from '../../hooks/useCrossTabSync';
import { callerIdService, logService, settingsService } from '../../services';
import { dispatchCallDisconnected } from '../../events/callEvents';
import { applyDeviceSettings } from '../../utils';
import { AgentContext } from '../AgentContext';
import { CallWithDuration, MyPhoneCall } from '../../types';
import { setSelectedCallId, clearSelectedCallId } from '../../utils/appStorage';

import { TwilioContext, TwilioDeviceStatus, BackendAgentStatus } from './TwilioContext';

interface TwilioContextProviderProps {
  children: React.ReactNode;
}

/* istanbul ignore next */
const TwilioContextProvider: FC<TwilioContextProviderProps> = ({ children }) => {
  const [device, setDevice] = useState<Device>();
  const [status, setStatus] = useState<TwilioDeviceStatus>('disconnected');
  const [agentStatus, setAgentStatusInternal] = useState<BackendAgentStatus>('offline');
  const [isStatusLocked, setIsStatusLocked] = useState(false);
  const { identity, phoneNumber } = useContext(AgentContext);
  const [selectedHistoricalCall, setSelectedHistoricalCallState] = useState<MyPhoneCall | undefined>();
  const initialStatusFetched = useRef(false);
  const agentStatusRef = useRef<BackendAgentStatus>('offline');

  // Keep agentStatusRef in sync with agentStatus state
  useEffect(() => {
    agentStatusRef.current = agentStatus;
  }, [agentStatus]);

  const fetchAndUpdateAgentStatus = useCallback(async(): Promise<BackendAgentStatus | null> => {
    if (!identity) return null;

    try {
      const response = await getAgentStatus(identity);
      const newStatus = (response.status as BackendAgentStatus) || 'offline';
      setAgentStatusInternal(newStatus);
      setIsStatusLocked(newStatus === 'on-a-call');
      return newStatus;
    } catch (error) {
      logService.addLog(`Failed to fetch agent status: ${error.message}`);
      return null;
    }
  }, [identity]);

  const handleCrossTabStatusChange = useCallback(
    async(newStatus: BackendAgentStatus) => {
      setAgentStatusInternal(newStatus);
      setIsStatusLocked(newStatus === 'on-a-call');
      fetchAndUpdateAgentStatus();

      // Auto-connect Twilio device when another tab goes online
      if (newStatus === 'online' && status === 'disconnected' && identity && phoneNumber) {
        logService.addLog('Another tab went online. Connecting Twilio device...');
        setStatus('connecting');
        try {
          const token = await getUserTwilioToken(identity);
          await initDevice(token);
          setStatus('connected');
          logService.addLog('Twilio device connected. You can receive calls in this tab.');
        } catch (error) {
          setStatus('disconnected');
          logService.addLog(`Failed to connect Twilio device: ${error.message}`);
        }
      }
    },
    [fetchAndUpdateAgentStatus, status, identity, phoneNumber],
  );

  const { broadcastStatus } = useCrossTabSync({
    identity: identity || '',
    onStatusChange: handleCrossTabStatusChange,
  });

  useEffect(() => {
    if (!identity || !phoneNumber || initialStatusFetched.current) return;

    const fetchInitialStatusAndReconnect = async() => {
      try {
        const response = await getAgentStatus(identity);
        const backendStatus = (response.status as BackendAgentStatus) || 'offline';
        setAgentStatusInternal(backendStatus);
        setIsStatusLocked(backendStatus === 'on-a-call');
        initialStatusFetched.current = true;

        if (backendStatus === 'on-a-call') {
          logService.addLog('You are currently on a call in another tab or device.');
        } else if (backendStatus === 'online') {
          logService.addLog('Reconnecting Twilio device...');
          setStatus('connecting');
          try {
            const token = await getUserTwilioToken(identity);
            await initDevice(token);
            setStatus('connected');
            logService.addLog('Twilio device reconnected. You are online and can receive calls.');
          } catch (error) {
            setStatus('disconnected');
            setAgentStatusInternal('dial-only');
            logService.addLog(`Failed to reconnect Twilio device: ${error.message}`);
          }
        }
      } catch (error) {
        logService.addLog(`Failed to fetch initial agent status: ${error.message}`);
      }
    };

    fetchInitialStatusAndReconnect();
  }, [identity, phoneNumber]);

  useEffect(() => {
    if (!identity) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchAndUpdateAgentStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [identity, fetchAndUpdateAgentStatus]);

  // Poll agent status every 30 seconds as backup
  useEffect(() => {
    if (!identity) return;

    const interval = setInterval(fetchAndUpdateAgentStatus, 30000);
    return () => clearInterval(interval);
  }, [identity, fetchAndUpdateAgentStatus]);

  // Wrap setSelectedHistoricalCall to also save ID to localStorage
  const setSelectedHistoricalCall = useCallback((call: MyPhoneCall | undefined) => {
    setSelectedHistoricalCallState(call);
    if (call) {
      setSelectedCallId(call.id);
    } else {
      clearSelectedCallId();
    }
  }, []);

  const { calls, currentCall, forwardCall, setCalls, setCurrentCall } = useTwilioCalls(device);

  useEffect(() => {
    if (calls.length === 0) return;

    const handleCallAccepted = async() => {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const newStatus = await fetchAndUpdateAgentStatus();
      if (newStatus) broadcastStatus(newStatus);
    };

    const handleCallDisconnected = async(call: CallWithDuration) => {
      const callSid = call.parameters?.CallSid;

      await new Promise((resolve) => setTimeout(resolve, 2000));
      const newStatus = await fetchAndUpdateAgentStatus();
      if (newStatus) broadcastStatus(newStatus);

      if (callSid) {
        dispatchCallDisconnected(callSid);
      }

      await new Promise((resolve) => setTimeout(resolve, 3000));
      setCalls((prevCalls) => prevCalls.filter((c) => c.parameters?.CallSid !== callSid));
    };

    calls.forEach((call) => {
      call.on('accept', handleCallAccepted);
      call.on('disconnect', () => handleCallDisconnected(call as CallWithDuration));
    });

    return () => {
      calls.forEach((call) => {
        call.off('accept', handleCallAccepted);
        call.off('disconnect', handleCallDisconnected);
      });
    };
  }, [calls, broadcastStatus, fetchAndUpdateAgentStatus, setCalls]);

  // Send heartbeat every 5 minutes when agent is online
  useAgentHeartbeat(identity, phoneNumber, status === 'connected');

  // Handle device unregistered event (connection lost due to token expiry, network issues, etc.)
  useEffect(() => {
    if (!device || !identity) return;

    const handleUnregistered = async() => {
      logService.addLog('Device unregistered - connection lost');

      // Only attempt reconnection if we were supposed to be online
      if (agentStatusRef.current === 'online') {
        logService.addLog('Attempting to reconnect...');
        setStatus('connecting');

        try {
          const token = await getUserTwilioToken(identity);
          device.updateToken(token);
          await device.register();
          setStatus('connected');
          logService.addLog('Reconnected successfully');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          logService.addLog(`Failed to reconnect: ${errorMessage}`);
          // Fall back to dial-only state
          setStatus('disconnected');
          setAgentStatusInternal('dial-only');
          setIsStatusLocked(false);
          broadcastStatus('dial-only');

          if (phoneNumber) {
            try {
              await updateAgentPhoneAssignee({
                identity,
                phoneNumber,
                status: 'dial-only',
              });
            } catch (updateError) {
              const updateErrorMessage = updateError instanceof Error ? updateError.message : 'Unknown error';
              logService.addLog(`Error updating status: ${updateErrorMessage}`);
            }
          }
        }
      } else {
        // Not supposed to be online, just update status properly
        setStatus('disconnected');
        logService.addLog('Device disconnected');
      }
    };

    device.on('unregistered', handleUnregistered);

    return () => {
      device.off('unregistered', handleUnregistered);
    };
  }, [device, identity, phoneNumber, broadcastStatus]);

  /* istanbul ignore next */
  const makeCallWithLogs = async(to: string): Promise<Call | undefined> => {
    try {
      logService.addLog(`Attempting to make call to: ${to}`);

      let deviceInstance = device;
      if (!deviceInstance) {
        logService.addLog('No device initialized, will initialize directly for dial only');
        try {
          if (!identity) {
            logService.addLog('Error: Cannot initiate call — missing valid identity.');
            return undefined;
          }

          setStatus('connecting');
          const token = await getUserTwilioToken(identity);

          // Only update phone assignee if we have a phone number
          if (phoneNumber) {
            await updateAgentPhoneAssignee({
              identity,
              phoneNumber,
              status: 'dial-only',
            });
          }
          deviceInstance = await initDevice(token);
          setStatus('connected');
        } catch (error) {
          setStatus('disconnected');
          logService.addLog(`Error initializing device directly: ${error.message}`);
          console.error('Error initializing device directly:', error);
          return undefined;
        }
      } else {
        logService.addLog('Device is already initialized, proceeding with call');
      }

      try {
        if (!deviceInstance) {
          logService.addLog('No device initialized, will initialize directly for dial only');
          return undefined;
        }

        // Get custom caller ID and line name if available
        const { callerId: customCallerId, lineName } = await callerIdService.getCallerId(to);
        const params: Record<string, string> = { To: to };

        // Determine the caller ID being used (custom or default)
        const usedCallerId = customCallerId || phoneNumber; // Use the default phone number if no custom one
        const usedLineName = lineName; // Store the line name

        // Add custom caller ID if available
        if (customCallerId) {
          params.CustomCallerId = customCallerId;
          logService.addLog(`Using custom caller ID: ${customCallerId}${lineName ? ` (${lineName})` : ''}`);
        }

        const call = await deviceInstance.connect({ params });
        logService.addLog(`Call initiated successfully to: ${to}`);

        // Broadcast status change when outbound call starts ringing
        call.on('ringing', async() => {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const newStatus = await fetchAndUpdateAgentStatus();
          if (newStatus) broadcastStatus(newStatus);
        });

        // Now manually add this call to our calls state
        const durationInitial = Date.now();
        const callWithDuration = Object.assign(call, {
          durationInitial,
          usedCallerId, // Store which caller ID was actually used
          usedLineName, // Store which line name was actually used
        });

        // Manually update calls state
        setCalls((prevCalls) => [callWithDuration, ...prevCalls]);
        // Make sure we're setting the enhanced call object with the caller ID
        setCurrentCall(callWithDuration);

        // Set up disconnect handler
        call.on('disconnect', () => {
          const durationFinal = Date.now();
          const updatedCall = Object.assign(call, { durationFinal });

          // Update calls list on disconnect
          setCalls((prevCalls) => {
            const updatedCalls = [...prevCalls];
            const callIndex = updatedCalls.findIndex((c) => c === call);
            if (callIndex >= 0) {
              updatedCalls[callIndex] = updatedCall;
            }
            return updatedCalls;
          });
        });

        return callWithDuration;
      } catch (err) {
        logService.addLog(`Error making direct call: ${err.message}`);
        console.error('Error making direct call:', err);
        return undefined;
      }
    } catch (error) {
      const message = `Error while calling to: ${to} \n${error}`;
      logService.addLog(message);
      console.error('Call error:', error);
      return undefined;
    }
  };

  /* istanbul ignore next */
  const addDeviceListeners = (device: Device) => {
    settingsService.on('change', () => {
      applyDeviceSettings(device);
    });
    device.on('tokenWillExpire', async() => {
      const token = await getUserTwilioToken(device.identity as unknown as string);
      device.updateToken(token);
    });

    device.on('error', (error: Error) => {
      logService.addLog(`Error: ${error.message}`);
    });
  };

  /* istanbul ignore next */
  const initDevice = async(token: string) => {
    try {
      logService.addLog('Starting the device.');
      const additionalDeviceParams = (TWILIO_SIGNALING_ENDPOINT ? {
        chunderw: TWILIO_SIGNALING_ENDPOINT,
        eventgw: TWILIO_SIGNALING_ENDPOINT,
      } : {});
      const deviceInstance = new Device(token, {
        ...additionalDeviceParams,
        allowIncomingWhileBusy: true,
        closeProtection: true,
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      addDeviceListeners(deviceInstance);
      await deviceInstance.register();

      await applyDeviceSettings(deviceInstance);

      setDevice(deviceInstance);
      return deviceInstance; // Return the device instance in case we need it
    } catch (error) {
      logService.addLog(`Error initializing device: ${error.message}`);
      console.error('Error initializing device:', error);
      throw error; // Re-throw to let the caller handle it
    }
  };

  /* istanbul ignore next */
  const connect = async(identity: string, phoneNumber: string) => {
    try {
      if (!identity) {
        logService.addLog('Error: Cannot connect — missing valid identity.');
        return;
      }

      if (!phoneNumber) {
        logService.addLog('Error: Cannot conncet — missing valid phone number.');
        return;
      }

      setStatus('connecting');

      const token = await getUserTwilioToken(identity);

      await updateAgentPhoneAssignee({
        identity,
        phoneNumber,
        status: 'online',
      });
      await initDevice(token);

      setStatus('connected');
      setAgentStatusInternal('online');
      setIsStatusLocked(false);
      broadcastStatus('online');
      logService.addLog('You are now online and available to receive calls.');
    } catch (error) {
      setStatus('disconnected');
      setAgentStatusInternal('dial-only');
      setIsStatusLocked(false);
      logService.addLog(`Connection error: ${error.message}`);
    }
  };

  /* istanbul ignore next */
  const disconnect = async() => {
    setStatus('disconnected');
    setAgentStatusInternal('dial-only');
    setIsStatusLocked(false);
    broadcastStatus('dial-only');

    if (identity && phoneNumber) {
      try {
        await updateAgentPhoneAssignee({
          identity,
          phoneNumber,
          status: 'dial-only',
        });
      } catch (error) {
        logService.addLog(`Error updating status on disconnect: ${error.message}`);
      }
    }

    await device?.destroy();

    logService.addLog('You are now offline and will not receive calls.');
    setDevice(undefined);
  };

  const contextValue = {
    connect,
    disconnect,
    status,
    agentStatus,
    isStatusLocked,
    setStatus,
    calls,
    currentCall,
    makeCall: makeCallWithLogs,
    forwardCall,
    setCurrentCall,
    selectedHistoricalCall,
    setSelectedHistoricalCall,
  };

  return <TwilioContext.Provider value={contextValue}>{children}</TwilioContext.Provider>;
};

export default TwilioContextProvider;
